import { record } from 'fp-ts';
import styled from 'styled-components';
import { OnboardingAuditStatus, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { WithEnterpriseId } from '@mews-commander/types';
import { QueryBoundary } from '@mews-commander/ui';
import { ChartData, ProgressChart } from '@mews/b2b-ui';
import { lt } from '@mews/framework';
import { getSpacingToken } from '@optimus-web/common';
import { Card, Container, Stack, Typography } from '@optimus-web/core';
import { OverallStatusTag } from '../components/AuditStatusTag';
import { AuditCheckResults, collateStatusFromFieldsArray, flattenAudits } from '../setupTab/utils/auditTransforms';
import { isAuditMewsUniversityCoursesCompleted, isAuditUnpaidInvoiceOverview } from './utils';
import { RequestGoLiveButton } from './RequestGoLiveButton';

// Derive the width of the sidebar from a multiple of an existing spacing token
// TODO as we iterate we'll think more about how to layout the page inline with standard patterns
//      but at least we have less magic numbers and minWidths/heights now
const PropertyInfoContainer = styled(Container)`
    min-width: ${(props) => getSpacingToken('1000')(props) * 4}px;
`;

const useAuditInfo = (enterpriseId: string) => {
    const {
        data: { legalEnvironmentData, kycStatus, ...auditChecks },
    } = useRepositorySuspenseQuery('enterprises', 'getOnboardingAudit', { enterpriseId });

    const allAudits = flattenAudits(auditChecks);
    const overallStatus = collateStatusFromFieldsArray(allAudits);

    const auditNames = record.keys(auditChecks);
    const totalAudits = auditNames.length;

    const totalComplete = countByStatus(auditNames, auditChecks, OnboardingAuditStatus.Completed);

    const progressChartData: ChartData[] = [
        {
            id: 'audit-progress',
            name: lt('GoLiveAuditsPropertyInfoChartTasksPassed'),
            value: totalComplete,
            color: 'blue',
        },
    ];

    const invoiceAudit = allAudits.find((audit) => isAuditUnpaidInvoiceOverview(audit));
    const universityAudit = allAudits.find((audit) => isAuditMewsUniversityCoursesCompleted(audit));

    return {
        invoiceAuditStatus: invoiceAudit?.status,
        kycStatus,
        totalAudits,
        overallStatus,
        progressChartData,
        universityAuditStatus: universityAudit?.status,
    };
};

const countByStatus = (
    auditNames: Array<keyof AuditCheckResults>,
    audits: AuditCheckResults,
    status: OnboardingAuditStatus
) => auditNames.filter((audit) => audits[audit].status === status).length;

const PropertyInfoContents = ({ enterpriseId }: WithEnterpriseId) => {
    const { invoiceAuditStatus, kycStatus, totalAudits, overallStatus, progressChartData, universityAuditStatus } =
        useAuditInfo(enterpriseId);

    return (
        <Stack vertical spacing="200">
            <Stack vertical spacing="50">
                <Typography textStyle="bodyLargeStrong" as="div">
                    {lt('Progress')}
                </Typography>
                <ProgressChart
                    total={totalAudits}
                    data={progressChartData}
                    valueFormatter={(value) => (
                        <Typography textStyle="bodyLarge" as="div">{`${value} / ${totalAudits}`}</Typography>
                    )}
                    legend={{ showValues: true }}
                />
            </Stack>
            <Stack fill distribution="space-between">
                <Typography textStyle="bodyMedium" as="div">
                    {lt('GoLiveAuditsPropertyInfoKycResult')}
                </Typography>
                <OverallStatusTag status={kycStatus.status} />
            </Stack>
            <Stack fill distribution="space-between">
                <Typography textStyle="bodyMedium" as="div">
                    {lt('GoLiveAuditsPropertyInfoMewsSubscriptionPaid')}
                </Typography>
                {invoiceAuditStatus ? <OverallStatusTag status={invoiceAuditStatus} /> : null}
            </Stack>
            <Stack fill distribution="space-between">
                <Typography textStyle="bodyMedium" as="div">
                    {lt('GoLiveAuditsPropertyInfoAuditResult')}
                </Typography>
                <OverallStatusTag status={overallStatus} />
            </Stack>
            <Stack fill distribution="space-between">
                <Typography textStyle="bodyMedium" as="div">
                    {lt('MewsUniversity')}
                </Typography>
                {universityAuditStatus ? <OverallStatusTag status={universityAuditStatus} /> : null}
            </Stack>
            <RequestGoLiveButton status={overallStatus} />
        </Stack>
    );
};

/** Top level Property Info container as a card and query boundary */
export const PropertyInfo = ({ enterpriseId }: WithEnterpriseId) => (
    <Card>
        <PropertyInfoContainer padding="300">
            <QueryBoundary loader="inline" showError>
                <PropertyInfoContents enterpriseId={enterpriseId} />
            </QueryBoundary>
        </PropertyInfoContainer>
    </Card>
);
